import React from 'react';
import "./Login.css";
import AuthService from "../../../services/auth-service/AuthService";
import NotificationService from "../../../services/notification-service/NotificationService";
import {onlyUnique} from "../../../utils/ArrayUtil";
import {passwordNotValid} from "../../../services/auth-service/PasswordValidator";
import PasswordInput from "../../../components/text-input/PasswordInput";

class LoginPopup extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            username: this.props.username,
            password: ' ',
            submitted: false,
            mfaRequired: false,
            mfaToken: '',
            resendCountdown: 0,
            resendError: null
        };

        this.handleResendToken = this.handleResendToken.bind(this);
        this.handleMfaSubmit = this.handleMfaSubmit.bind(this);
        this.countdownTimer = null;
    }

    componentWillUnmount() {
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer);
        }
    }

    handleChange(e) {
        const {name, value} = e.target;
        this.setState({[name]: value});
    }

    startCountdown() {
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer);
        }
        this.countdownTimer = setInterval(() => {
            this.setState(prevState => {
                const newCountdown = prevState.resendCountdown - 1;
                if (newCountdown <= 0) {
                    clearInterval(this.countdownTimer);
                    return { resendCountdown: 0 };
                }
                return { resendCountdown: newCountdown };
            });
        }, 1000);
    }

    handleResendToken() {
        const { username, password } = this.state;

        this.setState({ resendError: null });

        // Resend = call login again without token
        AuthService.login(username, password, null)
            .then(data => {
                if (data.mfaRequired) {
                    this.setState({ resendCountdown: 60 });
                    this.startCountdown();
                }
            })
            .catch(error => {
                console.error('Resend error:', error);
                this.setState({ resendError: error });
            });
    }

    handleMfaSubmit(e) {
        e.preventDefault();
        const {username, password, mfaToken} = this.state;

        if (mfaToken?.length === 6) {
            AuthService.login(username, password, mfaToken)
                .then(
                    user => {
                        AuthService.retrieveUserData(userData => {
                            Object.assign(user, userData);
                            localStorage.setItem('user', JSON.stringify(user));
                            localStorage.setItem('clinics', JSON.stringify(user.clinics.map(c => c.id)));
                            localStorage.setItem('countries', JSON.stringify(user.clinics.map(c => c.country).filter(onlyUnique)));
                            this.props.close();
                        });
                    },
                    error => {
                        NotificationService.showNotification({
                            severity: 'error',
                            summary: 'Authentication Error',
                            detail: error
                        });
                    }
                );
        }
    }

    handleSubmit(e) {
        e.preventDefault();
        this.setState({submitted: true});
        const {username, password} = this.state;

        if (passwordNotValid(password, true)) return;

        AuthService.login(username, password, null)
            .then(
                user => {
                    // Check if MFA is required
                    if (user.mfaRequired) {
                        console.log('[LoginPopup] MFA required, showing verification form');
                        this.setState({
                            mfaRequired: true,
                            resendCountdown: 60
                        });
                        this.startCountdown();
                        return;
                    }

                    // Regular login successful
                    console.log('[LoginPopup] Login successful, retrieving user data');
                    AuthService.retrieveUserData(userData => {
                        Object.assign(user, userData);
                        localStorage.setItem('user', JSON.stringify(user));
                        localStorage.setItem('clinics', JSON.stringify(user.clinics.map(c => c.id)));
                        localStorage.setItem('countries', JSON.stringify(user.clinics.map(c => c.country).filter(onlyUnique)));
                        this.props.close();
                    });
                },
                error => {
                    NotificationService.showNotification({
                        severity: 'error',
                        summary: 'Authentication Error',
                        detail: error || 'Bad Credentials'
                    });
                }
            );

    }

    render() {
        const {username, password, submitted, mfaRequired, mfaToken, resendCountdown, resendError} = this.state;

        if (mfaRequired) {
            // MFA verification form
            return (
                <div className="login-page h-100">
                    <div className="col-md-3 col-md-offset-3 login-form">
                        <h4 className="mb-3">Multi-Factor Authentication</h4>
                        <p className="text-muted mb-4">
                            We've sent a 6-digit verification code to your email.
                            Please enter it below to complete your login.
                        </p>
                        <form name="mfaForm" onSubmit={this.handleMfaSubmit}>
                            <div className={'form-group' + (submitted && !mfaToken ? ' has-error' : '')}>
                                <label htmlFor="mfaToken">Verification Code</label>
                                <input
                                    type="text"
                                    className="form-control text-center"
                                    name="mfaToken"
                                    value={mfaToken}
                                    onChange={this.handleChange.bind(this)}
                                    maxLength="6"
                                    placeholder="000000"
                                    autoFocus
                                    style={{fontSize: '1.5rem', letterSpacing: '0.5rem'}}
                                />
                                {submitted && !mfaToken &&
                                    <div className="help-block">Verification code is required</div>
                                }
                            </div>
                            <div className="form-group">
                                <small className="text-muted">
                                    The code will expire in 10 minutes
                                </small>
                            </div>
                            {resendError && (
                                <div className="alert alert-warning" role="alert">
                                    {resendError}
                                </div>
                            )}
                            <div className="form-group mt-4">
                                <button className="btn btn-success btn-block">Verify</button>
                            </div>
                            <div className="form-group text-center">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={this.handleResendToken}
                                    disabled={resendCountdown > 0}
                                >
                                    {resendCountdown > 0
                                        ? `Send Again (${resendCountdown}s)`
                                        : 'Send Again'}
                                </button>
                            </div>
                            <div className="form-group text-center">
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    onClick={() => this.setState({mfaRequired: false, mfaToken: ''})}
                                >
                                    Back to login
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            );
        }

        // Regular login form
        return (
            <div className="login-page h-100">
                <div className="col-md-3 col-md-offset-3 login-form">
                    <form name="form" onSubmit={this.handleSubmit.bind(this)}>
                        <div className='form-group'>
                            <label htmlFor="username">E-mail</label>
                            <input type="text" className="form-control" name="username" value={username} readOnly/>
                        </div>
                        <div className={'form-group' + (submitted && !password ? ' has-error' : '')}>
                            <label htmlFor="password">Password</label>
                            <PasswordInput className="form-control" name="password" value={password}
                                           onChange={this.handleChange.bind(this)}/>
                            {submitted && !password &&
                            <div className="help-block">Password is required</div>
                            }
                        </div>
                        <div className="form-group">
                            <button className="btn-success">Login</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }
}

export default LoginPopup;
